// src/pages/dashboard/user/aulasUser.js
import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { Card, Spinner, Alert } from "react-bootstrap";
import moment from "moment";
moment.locale("pt");

export default function AulasUser() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [aulas, setAulas] = useState([]);
    const [turmas, setTurmas] = useState([]);
    const [turmasAulas, setTurmasAulas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [activeView, setActiveView] = useState(() =>
        typeof window !== "undefined" && window.innerWidth >= 768 ? "calendario" : "lista"
    );
    const [showPast, setShowPast] = useState(false);

    const horaObjToHHmm = (obj) => {
        if (!obj) return "00:00";
        if (typeof obj === "string") {
            const [h, m] = obj.split(":");
            return `${h.padStart(2, "0")}:${(m || "00").padStart(2, "0")}`;
        }
        const h = String(obj.hours || obj.Hours || 0).padStart(2, "0");
        const m = String(obj.minutes || obj.Minutes || 0).padStart(2, "0");
        return `${h}:${m}`;
    };

    const fetchData = async () => {
        setLoading(true);
        setError("");
        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
        if (!token) { setError("Token não encontrado."); setLoading(false); return; }
        const headers = { Authorization: `Bearer ${token}` };
        try {
            const [aulasRes, turmasRes, turmasAulasRes] = await Promise.all([
                fetch(`${API_BASE_URL}/aulas`, { headers }),
                fetch(`${API_BASE_URL}/turmas`, { headers }),
                fetch(`${API_BASE_URL}/turmasaulas`, { headers }),
            ]);
            setAulas(aulasRes.ok ? await aulasRes.json() : []);
            setTurmas(turmasRes.ok ? await turmasRes.json() : []);
            setTurmasAulas(turmasAulasRes.ok ? await turmasAulasRes.json() : []);
        } catch (err) {
            console.error(err);
            setError("Erro ao carregar dados.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, []);

    const now = moment();

    const aulasEnriquecidas = aulas
        .map((a) => {
            const aulaId = a.id || a.Id;
            const mappings = turmasAulas.filter(
                (m) => Number(m.aulaId || m.AulaId) === Number(aulaId)
            );
            const turmasDaAula = mappings
                .map((m) => turmas.find((t) => Number(t.id || t.Id) === Number(m.turmaId || m.TurmaId)))
                .filter(Boolean);
            const turmaNames = turmasDaAula.map((t) => t.turma || t.nome || t.Nome).join(", ") || "Sem Turma";
            const startTime = horaObjToHHmm(a.hora_inicio);
            const endTime = horaObjToHHmm(a.hora_fim);
            const dataHora = moment(`${moment(a.data).format("YYYY-MM-DD")}T${startTime}`);
            return { ...a, aulaId, turmaNames, startTime, endTime, dataHora };
        })
        .sort((a, b) => a.dataHora - b.dataHora);

    const aulasFuturas = aulasEnriquecidas.filter(a => a.dataHora.isSameOrAfter(now));
    const aulasPassadas = aulasEnriquecidas.filter(a => a.dataHora.isBefore(now));

    const agruparPorMes = (lista) => {
        const grupos = {};
        lista.forEach(a => {
            const chave = a.dataHora.format("MMMM YYYY");
            if (!grupos[chave]) grupos[chave] = [];
            grupos[chave].push(a);
        });
        return grupos;
    };

    const gruposFuturos = agruparPorMes(aulasFuturas);
    const gruposPassados = agruparPorMes([...aulasPassadas].reverse());

    const calendarEvents = aulasEnriquecidas.map((a) => ({
        id: String(a.aulaId),
        title: a.turmaNames,
        start: `${moment(a.data).format("YYYY-MM-DD")}T${a.startTime}:00`,
        end: `${moment(a.data).format("YYYY-MM-DD")}T${a.endTime}:00`,
        backgroundColor: a.dataHora.isBefore(now) ? "#adb5bd" : "#1e1d45",
        borderColor: a.dataHora.isBefore(now) ? "#adb5bd" : "#1e1d45",
        extendedProps: { displayTime: `${a.startTime} – ${a.endTime}` },
    }));

    const AulaCard = ({ aula, isPast }) => {
        const isHoje = aula.dataHora.isSame(now, "day");
        const isAmanha = aula.dataHora.isSame(moment().add(1, "day"), "day");
        const diasRestantes = aula.dataHora.diff(now, "days");

        let badge = null;
        if (isHoje) badge = { label: "Hoje", bg: "#198754" };
        else if (isAmanha) badge = { label: "Amanhã", bg: "#0d6efd" };
        else if (!isPast && diasRestantes <= 6) badge = { label: `em ${diasRestantes + 1}d`, bg: "#fd7e14" };

        return (
            <div style={{
                display: "flex",
                alignItems: "stretch",
                backgroundColor: "white",
                borderRadius: 14,
                overflow: "hidden",
                marginBottom: 10,
                opacity: isPast ? 0.6 : 1,
                border: isHoje ? "2px solid #198754" : isAmanha ? "2px solid #0d6efd" : "1px solid #e9ecef",
                boxShadow: isPast ? "none" : "0 2px 8px rgba(0,0,0,0.07)",
            }}>
                {/* Coluna de data colorida */}
                <div style={{
                    width: 68,
                    minWidth: 68,
                    background: isPast ? "#adb5bd" : isHoje ? "#198754" : isAmanha ? "#0d6efd" : "#1e1d45",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "10px 4px",
                    color: "white",
                    textAlign: "center",
                }}>
                    <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, opacity: 0.85 }}>
                        {aula.dataHora.format("ddd")}
                    </span>
                    <span style={{ fontSize: 28, fontWeight: 900, lineHeight: 1, margin: "2px 0" }}>
                        {aula.dataHora.format("DD")}
                    </span>
                    <span style={{ fontSize: 10, opacity: 0.85 }}>
                        {aula.dataHora.format("MMM")}
                    </span>
                </div>

                {/* Conteúdo principal */}
                <div style={{ flex: 1, padding: "12px 14px", display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 5 }}>
                        <span style={{
                            fontWeight: 700, fontSize: 15, color: "#1e1d45",
                            lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                            {aula.turmaNames}
                        </span>
                        {badge && (
                            <span style={{
                                fontSize: 10, fontWeight: 700, color: "white",
                                backgroundColor: badge.bg, borderRadius: 20,
                                padding: "3px 8px", flexShrink: 0, whiteSpace: "nowrap",
                            }}>
                                {badge.label}
                            </span>
                        )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#6c757d", fontSize: 13 }}>
                        <span>{aula.startTime} – {aula.endTime}</span>
                    </div>
                </div>
            </div>
        );
    };

    const MesHeader = ({ mes, count, isPast }) => (
        <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 10, marginTop: 4,
            paddingBottom: 8, borderBottom: `2px solid ${isPast ? "#f0f0f5" : "#e8eaf6"}`,
        }}>
            <span style={{
                fontSize: 12, fontWeight: 800, textTransform: "uppercase",
                letterSpacing: 1, color: isPast ? "#adb5bd" : "#495057",
            }}>
                {mes}
            </span>
            <span style={{
                background: isPast ? "#adb5bd" : "#1e1d45", color: "white",
                borderRadius: 20, padding: "1px 8px", fontSize: 10, fontWeight: 700,
            }}>
                {count}
            </span>
        </div>
    );

    return (
        <>
            {/* Header */}
            <div className="d-flex justify-content-between flex-wrap gap-3 align-items-start py-4">
                <div>
                    <h1 className="h2">Calendário de Aulas</h1>
                    <p className="mb-0 text-muted">
                        {aulasFuturas.length > 0
                            ? `Próxima aula: ${aulasFuturas[0].dataHora.format("dddd, DD [de] MMMM")} às ${aulasFuturas[0].startTime}`
                            : "Sem aulas agendadas"}
                    </p>
                </div>
            </div>

            {error && <Alert variant="danger" onClose={() => setError("")} dismissible>{error}</Alert>}

            {loading ? (
                <div style={{ textAlign: "center", padding: "48px 0" }}>
                    <Spinner animation="border" style={{ color: "#1e1d45", width: 40, height: 40 }} />
                    <p style={{ marginTop: 12, color: "#6c757d", fontSize: 14 }}>A carregar aulas...</p>
                </div>
            ) : (
                <>
                    {/* Tab switcher */}
                    <div style={{
                        display: "flex", background: "#f0f0f5", borderRadius: 14,
                        padding: 4, marginBottom: 20,
                    }}>
                        {[{ key: "lista", label: "Lista" }, { key: "calendario", label: "Calendário" }].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveView(tab.key)}
                                style={{
                                    flex: 1, border: "none", borderRadius: 10,
                                    padding: "11px 0", fontWeight: 600, fontSize: 14, cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    backgroundColor: activeView === tab.key ? "white" : "transparent",
                                    color: activeView === tab.key ? "#1e1d45" : "#9ca3af",
                                    boxShadow: activeView === tab.key ? "0 2px 10px rgba(0,0,0,0.12)" : "none",
                                    fontFamily: "inherit",
                                }}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* ===== VISTA LISTA ===== */}
                    {activeView === "lista" && (
                        <>
                            {/* Stats resumo */}
                            <div style={{
                                display: "grid", gridTemplateColumns: "repeat(3, 1fr)",
                                gap: 10, marginBottom: 24,
                            }}>
                                {[
                                    { label: "Próximas", value: aulasFuturas.length, color: "#1e1d45" },
                                    { label: "Este mês", value: aulasEnriquecidas.filter(a => a.dataHora.isSame(now, "month")).length, color: "#0d6efd" },
                                    { label: "Realizadas", value: aulasPassadas.length, color: "#6c757d" },
                                ].map(s => (
                                    <div key={s.label} style={{
                                        background: "white", borderRadius: 14, padding: "14px 8px",
                                        textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
                                        borderTop: `4px solid ${s.color}`,
                                    }}>
                                        <div style={{ fontSize: 24, fontWeight: 900, color: s.color, lineHeight: 1 }}>
                                            {s.value}
                                        </div>
                                        <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>
                                            {s.label}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Aulas futuras */}
                            {Object.keys(gruposFuturos).length === 0 ? (
                                <div style={{
                                    textAlign: "center", padding: "48px 20px",
                                    background: "white", borderRadius: 16, boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                                }}>
                                    <p style={{ color: "#adb5bd", margin: 0, fontWeight: 600 }}>Sem aulas agendadas.</p>
                                </div>
                            ) : (
                                Object.entries(gruposFuturos).map(([mes, lista]) => (
                                    <div key={mes} style={{ marginBottom: 28 }}>
                                        <MesHeader mes={mes} count={lista.length} isPast={false} />
                                        {lista.map(a => <AulaCard key={a.aulaId} aula={a} isPast={false} />)}
                                    </div>
                                ))
                            )}

                            {/* Toggle aulas passadas */}
                            {aulasPassadas.length > 0 && (
                                <>
                                    <button
                                        onClick={() => setShowPast(v => !v)}
                                        style={{
                                            width: "100%", border: "2px dashed #dee2e6",
                                            borderRadius: 12, background: "transparent",
                                            color: "#6c757d", padding: "13px",
                                            fontSize: 13, fontWeight: 600, cursor: "pointer",
                                            marginBottom: 16, letterSpacing: 0.3,
                                        }}
                                    >
                                        {showPast ? "Ocultar aulas passadas" : `Ver ${aulasPassadas.length} aulas passadas`}
                                    </button>
                                    {showPast && Object.entries(gruposPassados).map(([mes, lista]) => (
                                        <div key={mes} style={{ marginBottom: 28 }}>
                                            <MesHeader mes={mes} count={lista.length} isPast={true} />
                                            {lista.map(a => <AulaCard key={a.aulaId} aula={a} isPast={true} />)}
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    )}

                    {/* ===== VISTA CALENDÁRIO ===== */}
                    {activeView === "calendario" && (
                        <Card border="light" className="shadow-sm mb-4">
                            <Card.Body className="p-2 p-md-3">
                                <FullCalendar
                                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                    initialView="dayGridMonth"
                                    headerToolbar={{ left: "prev,next", center: "title", right: "today" }}
                                    footerToolbar={{ center: "dayGridMonth,timeGridWeek,timeGridDay" }}
                                    events={calendarEvents}
                                    editable={false}
                                    selectable={false}
                                    height="auto"
                                    locale="pt"
                                    eventContent={(eventInfo) => (
                                        <div style={{ padding: "2px 4px", overflow: "hidden" }}>
                                            <div style={{ fontWeight: 700, fontSize: 11, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {eventInfo.event.title}
                                            </div>
                                            <div style={{ fontSize: 10, opacity: 0.85 }}>
                                                {eventInfo.event.extendedProps.displayTime}
                                            </div>
                                        </div>
                                    )}
                                />
                            </Card.Body>
                        </Card>
                    )}
                </>
            )}
        </>
    );
}
